import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { MIN_LOADING_DURATION_MS } from "@/constants/analysis";
import { Card } from "./card";
import { Progress } from "./progress";
import { LOADING_DURATIONS } from "@/config/loadingDurations";

interface LoadingServiceIdentificationProps {
  onComplete?: () => void;
}

interface LoadingStep {
  id: number;
  text: string;
  delay: number;
  completed: boolean;
}

const LOADING_STEPS_CONFIG = [
  {
    id: 1,
    text: "Verificare CAEN principal și CAEN-uri secundare",
    delay: LOADING_DURATIONS.serviceIdSteps[0],
  },
  {
    id: 2,
    text: "Analiză avansată a listelor oficiale",
    delay: LOADING_DURATIONS.serviceIdSteps[1],
  },
  {
    id: 3,
    text: "Smart search din surse publice și interpretare cu AI",
    delay: LOADING_DURATIONS.serviceIdSteps[2],
  },
  {
    id: 4,
    text: "Alocare servicii conform NIS2 Anexa 1 și 2",
    delay: LOADING_DURATIONS.serviceIdSteps[3],
  },
  {
    id: 5,
    text: "Scor de încredere pentru fiecare serviciu din Anexa 1 și 2",
    delay: LOADING_DURATIONS.serviceIdSteps[4],
  },
];

const TOTAL_DURATION = LOADING_DURATIONS.serviceIdComplete;

export function LoadingServiceIdentification({
  onComplete,
}: LoadingServiceIdentificationProps) {
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>(
    LOADING_STEPS_CONFIG.map((step) => ({ ...step, completed: false }))
  );
  const [progress, setProgress] = useState(0);

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const timers = LOADING_STEPS_CONFIG.map((step, index) =>
      setTimeout(() => {
        setLoadingSteps((prev) =>
          prev.map((s) => (s.id === step.id ? { ...s, completed: true } : s))
        );
        setProgress(((index + 1) / LOADING_STEPS_CONFIG.length) * 100);
      }, step.delay)
    );

    const completeTimer = setTimeout(() => {
      onCompleteRef.current?.();
    }, MIN_LOADING_DURATION_MS);

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      clearTimeout(completeTimer);
    };
  }, []);

  const completedCount = loadingSteps.filter((s) => s.completed).length;
  const totalCount = loadingSteps.length;

  return (
    <div className="flex items-center justify-center w-full bg-background p-4 rounded-lg">
      <div className="w-full max-w-2xl space-y-4 md:space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Loader2 className="h-10 md:h-16 w-10 md:w-16 text-primary animate-spin" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">
              Identificare servicii în curs...
            </h2>
            <p className="text-muted-foreground text-sm md:text-lg">
              Se analizează serviciile entității conform algoritmului DNSC
            </p>
          </div>
        </div>

        {/* Progress Card */}
        <Card className="bg-muted/30 border-0">
          <div className="p-6 space-y-6">
            {/* Progress Header */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs md:text-sm font-medium">
                <span>
                  Progres evaluare: {completedCount}/{totalCount}
                </span>
                <span className="text-muted-foreground">
                  {Math.round(progress)}%
                </span>
              </div>
              <Progress value={progress} className="h-1 md:h-2" />
            </div>

            {/* Loading Steps */}
            <div className="space-y-3">
              {loadingSteps.map((step, index) => {
                const isActive =
                  !step.completed &&
                  (index === 0 || loadingSteps[index - 1].completed);
                const isPending = !step.completed && !isActive;

                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-3 transition-all duration-300 ${!step.completed && !isActive ? "opacity-50" : ""
                      }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {step.completed ? (
                        <CheckCircle className="h-4 md:h-5 w-4 md:w-5 text-ds-green-600 animate-scale-in" />
                      ) : isActive ? (
                        <Loader2 className="h-4 md:h-5 w-4 md:w-5 text-primary animate-spin" />
                      ) : (
                        <div className="w-4 md:w-5 h-4 md:h-5 rounded-full border-2 border-muted-foreground/30" />
                      )}
                    </div>
                    <p
                      className={`text-xs md:text-sm leading-relaxed ${step.completed
                          ? "text-foreground"
                          : isActive
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }`}
                    >
                      {step.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
